<script lang="ts">import { createEventDispatcher, getContext } from 'svelte';
import { spring } from 'svelte/motion';
/** If set button will act as a standard <a href=..tag. */
export let href = undefined;
/** Can be used in conjunction with href to set the <a target. */
export let target = undefined;
/** Whether the button is primary. */
export let primary = false;
/** Button size. */
export let size = 'regular';
/** Disabled state */
export let disabled = false;
/** Fluid width of the button */
export let fluid = false;
/** Should the button obey form validation */
export let formValidation = false;
// Get parent form disabled state (if exists)
const formDisabled = getContext('formDisabled');
// Dispatched when button is activated via keyboard or click
// no need to preventDefault on the event unless the href attribute is set
const dispatch = createEventDispatcher();
$: isDisabled = (formValidation && $formDisabled) || disabled;
function handleClick(event) {
    if (href === undefined) {
        event.preventDefault();
    }
    if (!formValidation || (!$formDisabled && !disabled)) {
        dispatch('action', event);
    }
}
function handleKeydown(event) {
    if (!formValidation &&
        !$formDisabled &&
        !disabled &&
        (event.code === 'Space' || event.code === 'Enter')) {
        event.preventDefault();
        dispatch('action', event);
    }
}
let hoverPos = spring({ x: 0, y: 0 }, {
    stiffness: 0.04,
    damping: 0.2,
});
function handleMousemove(event) {
    hoverPos.set({ x: event.offsetX, y: event.offsetY });
}
function handleMouseenter(event) {
    hoverPos.set({ x: event.offsetX, y: event.offsetY }, { hard: true });
}
</script>

<style type="scss">.button {
  --spacing: 4px;
  --radius: 8px;
  --gradient-size: 200px;
  position: relative;
  font-size: 14px;
  display: inline-flex;
  font-weight: 450;
  letter-spacing: -0.04px;
  justify-content: center;
  background-color: var(--light-blue);
  border-radius: var(--radius);
  padding: 10px 12px;
  color: var(--main-blue);
  text-decoration: none;
  user-select: none;
  -webkit-user-select: none;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
}
.button.primary {
  background-color: var(--main-blue);
  color: white;
}
.button.primary:active:not(.disabled) {
  filter: contrast(150%) brightness(105%);
}
.button:focus-visible {
  outline: 0;
  text-decoration: underline;
}
.button:focus, .button:hover:not(.disabled) {
  outline: 0;
  border-color: rgba(0, 0, 0, 0.15);
  position: relative;
}
.button:active:not(.disabled) {
  filter: brightness(105%);
}
.button.disabled {
  color: var(--always-white);
  pointer-events: none;
  cursor: default;
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: all !important;
}
.button.fluid {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.button.loading :global(.content .icon:not(.loading)) {
  display: none;
}
.button :global(*) {
  pointer-events: none;
}
.button .hover {
  position: absolute;
  transition: 140ms ease-in-out;
  transition-property: width, left, opacity;
  top: calc(var(--gradient-size) / -2);
  left: 0px;
  border-radius: var(--radius);
  background: radial-gradient(circle closest-side, white, transparent);
  width: 0px;
  height: var(--gradient-size);
  opacity: 0.15;
  mix-blend-mode: overlay;
}
.button:hover:not(.disabled) .hover {
  width: var(--gradient-size);
  left: calc(var(--gradient-size) / -2);
}
.button .content {
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
}
.button :global(.content > *) {
  margin-right: var(--spacing);
}
.button :global(.content > *:last-child) {
  margin-right: 0;
}
.button.size-large {
  --spacing: 8px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 550;
  letter-spacing: -0.18px;
  padding: 16px 32px;
}
.button.size-large .hover {
  --gradient-size: 500px;
}</style>

<a
    on:click={handleClick}
    on:keydown={handleKeydown}
    on:mousemove={handleMousemove}
    on:mouseenter={handleMouseenter}
    disabled={isDisabled}
    class={`button size-${size}`}
    class:disabled={isDisabled}
    class:fluid
    class:primary
    {href}
    {target}
    role="button"
    tabindex="0"
>
    {#if !isDisabled}
        <span class="hover" style={`transform: translate(${$hoverPos.x}px, ${$hoverPos.y}px)`} />
    {/if}
    <span class="content">
        <slot>Click me</slot>
    </span>
</a>
